import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router";
import {
  Plus,
  Link,
  Trash2,
  Music,
  PlayCircle,
  Clock,
  Timer,
  CheckCircle,
  Image,
  Volume2,
  VolumeX,
  Download,
  Upload,
} from "lucide-react";
import { Col, Input, Row, message } from "antd";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router";
import apiPath from "../../constants/apiPath";
import "../../assets/styles/dashboard.css";
import buildImage from "../../assets/images/build.jpeg";
import grindImage from "../../assets/images/grind.jpeg";
import fuseImage from "../../assets/images/fuse.jpeg";
import FontSelector from "../../components/FontSelector";

const AdminShadowView = () => {
  const { gymId } = useParams();
  const [activeDay, setActiveDay] = useState("MONDAY");
  const [fullWeekData, setFullWeekData] = useState(null);
  const fullWeekDataRef = useRef(fullWeekData);

  useEffect(() => {
    fullWeekDataRef.current = fullWeekData;
  }, [fullWeekData]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [isTableMode, setIsTableMode] = useState(false); // Preview state
  const [socket, setSocket] = useState(null);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [newBlockName, setNewBlockName] = useState("");
  const [newBlockTitle, setNewBlockTitle] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState("");
  const [currentMode, setCurrentMode] = useState("hero");
  const [localCommandSent, setLocalCommandSent] = useState(null);
  const [isDayTransitioning, setIsDayTransitioning] = useState(false);
  const [completionTopMessage, setCompletionTopMessage] =
    useState("Great Job!");
  const [completionMessage, setCompletionMessage] = useState(
    "You've just completed!",
  );
  const [workoutTransitionMessage, setWorkoutTransitionMessage] = useState("");
  const [isWorkoutActive, setIsWorkoutActive] = useState(false);
  const [isWorkoutPaused, setIsWorkoutPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textColor, setTextColor] = useState("#FCDC08");
  const [borderColor, setBorderColor] = useState("#FCDC08");
  const [font1, setFont1] = useState("'Horizon', sans-serif");
  const [font1Color, setFont1Color] = useState("#FCDC08");
  const [font2, setFont2] = useState("'Horizon', sans-serif");
  const [font2Color, setFont2Color] = useState("#FCDC08");
  const [tempTextColor, setTempTextColor] = useState("#FCDC08");
  const [tempBorderColor, setTempBorderColor] = useState("#FCDC08");
  const [tempFont1Color, setTempFont1Color] = useState("#FCDC08");
  const [tempFont2Color, setTempFont2Color] = useState("#FCDC08");
  const [backgroundColor, setBackgroundColor] = useState("#000000");
  const [selectedColor, setSelectedColor] = useState("#000000ff");
  const [tempColor, setTempColor] = useState(selectedColor);

  useEffect(() => {
    if (!fullWeekData?.full_week?.[activeDay]) {
      return;
    }

    const currentDayData = fullWeekData.full_week[activeDay];

    // Stop all audio when changing days and wait for cleanup
    const cleanupAndUpdate = async () => {
      setIsDayTransitioning(true);
      await stopAllAudio();
      setCurrentMode("hero");
      setIsWorkoutActive(false);
      setIsWorkoutPaused(false);
      // Short delay for React to remount audio elements
      await new Promise((resolve) => setTimeout(resolve, 150));
      setIsDayTransitioning(false);
      console.log("[DAY CHANGE] Transition complete");
    };

    cleanupAndUpdate();

    // Update theme color
    if (currentDayData.color) {
      setSelectedColor(currentDayData.color);
      setTempColor(currentDayData.color);
    }

    // Update text color
    if (currentDayData.text_color) {
      setTextColor(currentDayData.text_color);
      setTempTextColor(currentDayData.text_color);
    }

    // Update border color
    if (currentDayData.border_color) {
      setBorderColor(currentDayData.border_color);
      setTempBorderColor(currentDayData.border_color);
    }

    // Update background color
    if (currentDayData.background_color) {
      setBackgroundColor(currentDayData.background_color);
    }

    // Update font1 and font1_color
    if (currentDayData.font1) {
      setFont1(currentDayData.font1);
    }
    if (currentDayData.font1_color) {
      setFont1Color(currentDayData.font1_color);
      setTempFont1Color(currentDayData.font1_color);
    }

    // Update font2 and font2_color
    if (currentDayData.font2) {
      setFont2(currentDayData.font2);
    }
    if (currentDayData.font2_color) {
      setFont2Color(currentDayData.font2_color);
      setTempFont2Color(currentDayData.font2_color);
    }
  }, [activeDay, fullWeekData]);

  const handleColorChange = (color) => {
    console.log("handleColorChange called with:", color);
    setSelectedColor(color);
    setTempColor(color);

    // Persist to backend / local state through autoSave
    autoSave({ new_color: color });

    // Update local state immediately
    setFullWeekData((prev) => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      if (newData.full_week[activeDay]) {
        newData.full_week[activeDay].color = color;
      }
      return newData;
    });

    // Notify live clients via WebSocket
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(
          JSON.stringify({ type: "update_color", day: activeDay, color }),
        );
        message.success("Theme color updated and sent to live clients!");
      } catch (err) {
        message.error("Failed to send theme color");
      }
    } else {
      message.success("Theme color updated!");
    }
  };

  const handleTextColorChange = (color) => {
    console.log("handleTextColorChange called with:", color);
    setTempTextColor(color);
    setTextColor(color);

    // Persist to backend / local state through autoSave
    autoSave({ new_text_color: color });

    // Notify live clients via WebSocket
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(
          JSON.stringify({
            type: "update_text_color",
            day: activeDay,
            text_color: color,
          }),
        );
        message.success("Text color updated and sent to live clients!");
      } catch (err) {
        message.error("Failed to send text color");
      }
    } else {
      message.success("Text color updated!");
    }
  };

  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);

    // Persist to backend / local state through autoSave
    autoSave({ new_background_color: color });

    // Notify live clients via WebSocket
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(
          JSON.stringify({
            type: "update_background_color",
            day: activeDay,
            background_color: color,
          }),
        );
        message.success("Background color updated and sent to live clients!");
      } catch (err) {
        message.error("Failed to send background color");
      }
    } else {
      message.success("Background color updated!");
    }
  };

  const handleBorderColorChange = (color) => {
    setTempBorderColor(color);
    setBorderColor(color);

    // Persist to backend / local state through autoSave
    autoSave({ new_border_color: color });

    // Notify live clients via WebSocket
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(
          JSON.stringify({
            type: "update_border_color",
            day: activeDay,
            border_color: color,
          }),
        );
        message.success("Border color updated and sent to live clients!");
      } catch (err) {
        message.error("Failed to send border color");
      }
    } else {
      message.success("Border color updated!");
    }
  };

  // Combined Font 1 handler (font + color together)
  const handleFont1Apply = (font, color) => {
    setFont1(font);
    setFont1Color(color);
    setTempFont1Color(color);
    autoSave({ font1: font, font1_color: color });

    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(
          JSON.stringify({
            type: "update_font1",
            day: activeDay,
            font1: font,
            font1_color: color,
          }),
        );
        message.success("Font 1 updated and sent to live clients!");
      } catch (err) {
        message.error("Failed to send font 1");
      }
    } else {
      message.success("Font 1 updated!");
    }
  };

  // Combined Font 2 handler (font + color together)
  const handleFont2Apply = (font, color) => {
    setFont2(font);
    setFont2Color(color);
    setTempFont2Color(color);
    autoSave({ font2: font, font2_color: color });

    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(
          JSON.stringify({
            type: "update_font2",
            day: activeDay,
            font2: font,
            font2_color: color,
          }),
        );
        message.success("Font 2 updated and sent to live clients!");
      } catch (err) {
        message.error("Failed to send font 2");
      }
    } else {
      message.success("Font 2 updated!");
    }
  };

  // Audio states
  const [audioPlaying, setAudioPlaying] = useState({
    pre_hype: false,
    warmup: false,
    workout: false,
    preview: false,
  });
  const [audioVolume, setAudioVolume] = useState({
    pre_hype: 0.7,
    warmup: 0.7,
    workout: 0.7,
    preview: 0.7,
  });

  const audioRefs = {
    pre_hype: useRef(null),
    warmup: useRef(null),
    workout: useRef(null),
    preview: useRef(null),
  };

  // Refs to track current state values (avoid stale closures)
  const isWorkoutActiveRef = useRef(isWorkoutActive);
  const isWorkoutPausedRef = useRef(isWorkoutPaused);
  const currentModeRef = useRef(currentMode);
  const audioPlayingRef = useRef(audioPlaying);

  // Keep refs in sync with state
  useEffect(() => {
    isWorkoutActiveRef.current = isWorkoutActive;
  }, [isWorkoutActive]);

  useEffect(() => {
    isWorkoutPausedRef.current = isWorkoutPaused;
  }, [isWorkoutPaused]);

  useEffect(() => {
    currentModeRef.current = currentMode;
  }, [currentMode]);

  useEffect(() => {
    audioPlayingRef.current = audioPlaying;
  }, [audioPlaying]);

  const navigate = useNavigate();

  // Stop all audio except the specified type (returns promise for async handling)
  const stopOtherAudio = async (keepType) => {
    Object.keys(audioRefs).forEach((type) => {
      if (type !== keepType) {
        const audio = audioRefs[type].current;
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
          setAudioPlaying((prev) => ({ ...prev, [type]: false }));
        }
      }
    });
  };

  // Static available images - no need to fetch from backend
  const availableImages = [
    { value: "build", label: "Build", path: buildImage },
    { value: "grind", label: "Grind", path: grindImage },
    { value: "fuse", label: "Fuse", path: fuseImage },
  ];
  const fileInputs = {
    pre_hype_video: useRef(null),
    warmup_video: useRef(null),
    pre_hype_audio: useRef(null),
    warmup_audio: useRef(null),
    workout_audio: useRef(null),
    preview_audio: useRef(null),
    preview_video: useRef(null),
    background_image: useRef(null),
  };
  const DAYS = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];
  // NEW: Global save timers to track all pending saves
  const globalSaveTimers = useRef({});
  const pendingSavesRef = useRef({});
  // NEW: Debounced autoSave function
  const autoSave = useCallback(
    (updates) => {
      const token = localStorage.getItem("accessToken");

      // Update local state immediately
      setFullWeekData((prev) => {
        if (!prev) return prev;
        const newData = JSON.parse(JSON.stringify(prev));
        const dayData = newData.full_week[activeDay];
        if (updates.new_completion_message !== undefined) {
          if (!dayData.completion_settings) {
            dayData.completion_settings = {};
          }
          dayData.completion_settings.message = updates.new_completion_message;
          setCompletionMessage(updates.new_completion_message);
        }
        if (updates.new_completion_top_message !== undefined) {
          if (!dayData.completion_settings) {
            dayData.completion_settings = {};
          }
          dayData.completion_settings.top_message =
            updates.new_completion_top_message;
          setCompletionTopMessage(updates.new_completion_top_message);
        }
        if (updates.new_workout_transition_message !== undefined) {
          if (!dayData.completion_settings) {
            dayData.completion_settings = {};
          }
          dayData.completion_settings.workout_transition_message =
            updates.new_workout_transition_message;
          setWorkoutTransitionMessage(updates.new_workout_transition_message);
        }
        if (updates.new_title !== undefined) dayData.name = updates.new_title;
        if (updates.new_image !== undefined) {
          dayData.image = updates.new_image;
        }
        if (updates.new_color !== undefined) {
          dayData.color = updates.new_color;
        }
        if (updates.new_text_color !== undefined) {
          dayData.text_color = updates.new_text_color;
          setTextColor(updates.new_text_color);
        }

        // Update border color
        if (updates.new_border_color !== undefined) {
          dayData.border_color = updates.new_border_color;
          setBorderColor(updates.new_border_color);
        }

        // Update font1
        if (updates.font1 !== undefined) {
          dayData.font1 = updates.font1;
          setFont1(updates.font1);
        }

        // Update font1_color
        if (updates.font1_color !== undefined) {
          dayData.font1_color = updates.font1_color;
          setFont1Color(updates.font1_color);
        }

        // Update font2
        if (updates.font2 !== undefined) {
          dayData.font2 = updates.font2;
          setFont2(updates.font2);
        }

        // Update font2_color
        if (updates.font2_color !== undefined) {
          dayData.font2_color = updates.font2_color;
          setFont2Color(updates.font2_color);
        }

        // Legacy font family support
        if (updates.new_font_family !== undefined) {
          dayData.font1 = updates.new_font_family;
          setFont1(updates.new_font_family);
        }

        if (updates.new_background_color !== undefined) {
          dayData.background_color = updates.new_background_color;
          setBackgroundColor(updates.new_background_color);
        }
        if (updates.block && updates.block_data) {
          const blockKey = updates.block;
          dayData.data[blockKey] = {
            ...dayData.data[blockKey],
            ...updates.block_data,
            block_transition:
              updates.block_data.block_transition ??
              dayData.data[blockKey]?.block_transition ??
              0,
          };
          dayData.total_duration = calculateTotalDuration(dayData);
        }
        return newData;
      });

      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);

      // NEW: Create a unique key for this save (block-specific or general)
      const saveKey = updates.block || "general";

      // Store the pending save data
      pendingSavesRef.current[saveKey] = updates;

      // Clear any existing timer for this key
      if (globalSaveTimers.current[saveKey]) {
        clearTimeout(globalSaveTimers.current[saveKey]);
      }

      // Set new timer (5000ms delay)
      globalSaveTimers.current[saveKey] = setTimeout(async () => {
        try {
          const payload = {
            day: activeDay,
            ...pendingSavesRef.current[saveKey],
          };

          // Clear the pending save
          delete pendingSavesRef.current[saveKey];

          const response = await fetch(
            `${apiPath.baseURL}gym_owner/update_workout_plan`,
            {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            },
          );

          const result = await response.json();
          if (result.status !== 200) {
            if (result.error === "Nothing to update") {
            } else {
              message.error(result.error || "");
            }
          } else {
            const selectedImage = availableImages.find(
              (img) => img.value === updates.new_image,
            );
            if (result.updated_plan?.text_color) {
              setTempTextColor(result.updated_plan.text_color);
            }
            if (result.updated_plan?.border_color) {
              setTempBorderColor(result.updated_plan.border_color);
            }
            if (result.updated_plan?.font1) {
              setFont1(result.updated_plan.font1);
            }
            if (result.updated_plan?.font1_color) {
              setFont1Color(result.updated_plan.font1_color);
              setTempFont1Color(result.updated_plan.font1_color);
            }
            if (result.updated_plan?.font2) {
              setFont2(result.updated_plan.font2);
            }
            if (result.updated_plan?.font2_color) {
              setFont2Color(result.updated_plan.font2_color);
              setTempFont2Color(result.updated_plan.font2_color);
            }
          }
        } catch (err) {
          message.error("");
        }
      }, 5000); // Increased from 3000 to 5000ms
    },
    [activeDay],
  );
  // NEW: Clean up all timers on component unmount
  useEffect(() => {
    return () => {
      // Clear all timers
      Object.values(globalSaveTimers.current).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);
  // WebSocket: Control Stream in real-time
  useEffect(() => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let isMounted = true;
    let currentSocket = null;

    const connect = () => {
      // Don't connect if component is unmounted
      if (!isMounted) return;

      try {
        const token = localStorage.getItem("accessToken");
        // const userId = localStorage.getItem("userId"); // Get user ID
        const baseUrl = apiPath.baseURL
          .replace("/api/v1/", "/")
          .replace("http://", "ws://")
          .replace("https://", "wss://");
        // const ws = new WebSocket(`${baseUrl}ws/preview/`);
        const wsUrl = `${baseUrl}ws/admin-shadow/?token=${encodeURIComponent(token)}&gym_id=${gymId}`;
        const ws = new WebSocket(wsUrl);
        currentSocket = ws;
        ws.onopen = () => {
          setSocket(ws);
          reconnectAttempts = 0;
        };
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.day === activeDay) {
              setIsTableMode(data.type === "show_table");

              // Get current media from ref (avoids stale closure)
              const currentMedia =
                fullWeekDataRef.current?.full_week?.[activeDay]?.media || {};

              // Handle workout audio based on workout state
              if (
                data.type === "timer_update" ||
                data.type === "show_countdown"
              ) {
                // Workout started or countdown started - play workout audio if available
                if (currentMedia.workout_audio) {
                  const workoutEl = audioRefs.workout.current;
                  if (workoutEl && workoutEl.paused) {
                    if (
                      audioRefs.warmup.current &&
                      !audioRefs.warmup.current.paused
                    )
                      pauseAudio("warmup");
                    if (
                      audioRefs.pre_hype.current &&
                      !audioRefs.pre_hype.current.paused
                    )
                      pauseAudio("pre_hype");
                    playAudio("workout");
                  }
                }
              } else if (
                data.type === "workout_complete" ||
                data.type === "workout_stopped"
              ) {
                // Workout completed or stopped - stop workout audio
                // pauseAudio('workout');
              } else if (
                data.type === "pause_workout" ||
                data.type === "workout_paused"
              ) {
                // Ignore echo from our own pause command
                if (localCommandSent === "pause_workout") {
                  setLocalCommandSent(null);
                  return;
                }
                // Workout paused - pause workout audio
                pauseAudio("workout");
              } else if (
                data.type === "resume_workout" ||
                data.type === "workout_resumed"
              ) {
                // Ignore echo from our own resume command
                if (localCommandSent === "resume_workout") {
                  setLocalCommandSent(null);
                  return;
                }
                // Workout resumed - resume workout audio if available
                if (currentMedia.workout_audio) {
                  const workoutEl = audioRefs.workout.current;
                  if (workoutEl && workoutEl.paused) {
                    playAudio("workout");
                  }
                }
              }
              // Handle mode changes
              else if (data.type === "show_pre_hype") {
                // Ignore echo from our own command
                if (localCommandSent === "show_pre_hype") {
                  setLocalCommandSent(null);
                  return;
                }
                // When receiving show_pre_hype from backend, ensure pre-hype audio plays
                stopOtherAudio("pre_hype");
                setCurrentMode("pre_hype");
                if (currentMedia.pre_hype_audio) {
                  const preEl = audioRefs.pre_hype.current;
                  if (preEl && preEl.paused) {
                    playAudio("pre_hype");
                  }
                }
              } else if (data.type === "show_warmup") {
                // Ignore echo from our own command
                if (localCommandSent === "show_warmup") {
                  setLocalCommandSent(null);
                  return;
                }
                // When receiving show_warmup from backend, ensure warmup audio plays
                stopOtherAudio("warmup");
                setCurrentMode("warmup");
                if (currentMedia.warmup_audio) {
                  const warmEl = audioRefs.warmup.current;
                  if (warmEl && warmEl.paused) {
                    playAudio("warmup");
                  }
                }
              } else if (data.type === "show_preview") {
                if (localCommandSent === "show_preview") {
                  setLocalCommandSent(null);
                  return;
                }
                // When receiving show_preview from another source
                if (audioPlaying.pre_hype) pauseAudio("pre_hype");
                if (audioPlaying.warmup) pauseAudio("warmup");
                if (audioPlaying.workout) pauseAudio("workout");
                setCurrentMode("preview");
                if (currentMedia.preview_audio && !audioPlaying.preview) {
                  playAudio("preview");
                }
              } else if (data.type === "show_hero") {
                if (localCommandSent === "show_hero") {
                  setLocalCommandSent(null);
                  return;
                }
                // When going to hero mode, stop all audio
                setCurrentMode("hero");
                stopAllAudio();
              } else if (data.type === "show_table") {
                // When showing table, stop workout audio
                setCurrentMode("table");
                // const workoutEl = audioRefs.workout.current;
                // if (workoutEl && !workoutEl.paused) {
                //   pauseAudio("workout");
                // }
              }
              // Handle audio control commands
              else if (data.type === "stop_audio") {
                const audioType = data.payload?.audio_type;
                if (audioType) {
                  pauseAudio(audioType);
                }
              } else if (data.type === "play_audio") {
                const playAudioType = data.payload?.audio_type;
                if (playAudioType && currentMedia[`${playAudioType}_audio`]) {
                  // Stop other audio before playing new one
                  if (
                    playAudioType === "pre_hype" ||
                    playAudioType === "warmup" ||
                    playAudioType === "preview"
                  ) {
                    stopOtherAudio(playAudioType);
                  }
                  playAudio(playAudioType);
                }
              } else if (data.type === "stop_all_audio") {
                // Stop all audio
                stopAllAudio();
              }
            }
          } catch (err) {}
        };
        ws.onclose = (e) => {
          setSocket(null);
          currentSocket = null;

          // Only attempt reconnection if component is still mounted
          if (isMounted && reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            setTimeout(() => {
              if (isMounted) connect();
            }, 2000 * reconnectAttempts);
          } else {
          }
        };
        ws.onerror = (error) => {
          // Don't show error message on every attempt
          if (reconnectAttempts === 0) {
            message.error("Live sync connection failed");
          }
        };
      } catch (err) {}
    };
    connect();
    return () => {
      // Mark component as unmounted to prevent reconnection
      isMounted = false;

      // Close current WebSocket instance
      if (currentSocket && currentSocket.readyState === WebSocket.OPEN) {
        currentSocket.close(1000, "Component unmounting");
      }

      // Clear socket state
      setSocket(null);
      currentSocket = null;
    };
  }, [activeDay]);

  // Fetch Weekly Plan
  const fetchWeeklyPlan = async (dayToFetch = null) => {
    setLoading(true);
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("Login required");
      setLoading(false);
      return;
    }

    const targetDay = dayToFetch || activeDay;

    try {
      const res = await fetch(
        `${apiPath.baseURL}admin/gym_owner_workout_plan?gym_id=${gymId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await res.json();
      if (data.status === 200) {
        setFullWeekData(JSON.parse(JSON.stringify(data)));

        const currentDayData = data.full_week?.[targetDay];

        if (currentDayData?.completion_settings?.message) {
          console.log(
            "Found completion message:",
            currentDayData.completion_settings.message,
          );
          setCompletionMessage(currentDayData.completion_settings.message);
        } else {
          console.log("No completion message found, using default");
          setCompletionMessage("You've just completed!");
        }

        if (currentDayData?.completion_settings?.top_message) {
          setCompletionTopMessage(
            currentDayData.completion_settings.top_message,
          );
        } else {
          setCompletionTopMessage("You've just completed!");
        }

        if (currentDayData?.completion_settings?.workout_transition_message) {
          setWorkoutTransitionMessage(
            currentDayData.completion_settings.workout_transition_message,
          );
        } else {
          setWorkoutTransitionMessage("");
        }

        if (currentDayData?.text_color) {
          setTextColor(currentDayData.text_color);
          setTempTextColor(currentDayData.text_color);
        }
        if (currentDayData?.color) {
          setSelectedColor(currentDayData.color);
          setTempColor(currentDayData.color);
        }

        if (currentDayData?.background_color) {
          setBackgroundColor(currentDayData.background_color);
        }

        // Set border color from data
        if (currentDayData?.border_color) {
          setBorderColor(currentDayData.border_color);
          setTempBorderColor(currentDayData.border_color);
        }

        // Load dual fonts
        if (currentDayData?.font1) {
          setFont1(currentDayData.font1);
        }
        if (currentDayData?.font1_color) {
          setFont1Color(currentDayData.font1_color);
          setTempFont1Color(currentDayData.font1_color);
        }
        if (currentDayData?.font2) {
          setFont2(currentDayData.font2);
        }
        if (currentDayData?.font2_color) {
          setFont2Color(currentDayData.font2_color);
          setTempFont2Color(currentDayData.font2_color);
        }
      } else if (data.status === 403) {
        // Subscription invalid - show message and redirect to login
        message.error(
          data.message || "Subscription issue. Please log in again.",
        );

        // Clear local storage
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userData");

        // Redirect to login page
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      }
    } catch (err) {
      setError("Failed to load plan");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (fullWeekData?.full_week?.[activeDay]?.completion_settings?.message) {
      console.log(
        "Setting completion message from full_week:",
        fullWeekData.full_week[activeDay].completion_settings.message,
      );
      setCompletionMessage(
        fullWeekData.full_week[activeDay].completion_settings.message,
      );
    } else {
      console.log("No completion message found for", activeDay);
      setCompletionMessage("You've just completed!");
    }

    if (
      fullWeekData?.full_week?.[activeDay]?.completion_settings?.top_message
    ) {
      setCompletionTopMessage(
        fullWeekData.full_week[activeDay].completion_settings.top_message,
      );
    } else {
      setCompletionTopMessage("You've just completed!");
    }
    if (
      fullWeekData?.full_week?.[activeDay]?.completion_settings
        ?.workout_transition_message
    ) {
      setWorkoutTransitionMessage(
        fullWeekData.full_week[activeDay].completion_settings
          .workout_transition_message,
      );
    } else {
      setWorkoutTransitionMessage("");
    }
  }, [activeDay, fullWeekData]);
  useEffect(() => {
    // const today = new Date().toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
    // console.log("Today's day:", today);

    // setActiveDay(today);
    // fetchWeeklyPlan();
    const now = new Date();
    const options = {
      timeZone: "Australia/Sydney",
      weekday: "long",
    };

    // Get the day name in Sydney timezone
    const todayInSydney = now.toLocaleString("en-US", options).toUpperCase();

    // Also get the current hour in Sydney to help debug
    const timeOptions = {
      timeZone: "Australia/Sydney",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    const sydneyTime = now.toLocaleString("en-US", timeOptions);

    setActiveDay(todayInSydney);
    // Pass the Sydney day directly to fetchWeeklyPlan instead of relying on state
    fetchWeeklyPlan(todayInSydney);
  }, []);

  const currentDay = fullWeekData?.full_week?.[activeDay] || {};
  const media = currentDay.media || {};
  // Audio control functions with improved error handling and stability

  const playAudio = async (type) => {
    if (isDayTransitioning) {
      console.log(
        `[AUDIO] Blocked ${type} playback - day transition in progress`,
      );
      return false;
    }

    const audio = audioRefs[type].current;

    console.log(`[AUDIO] Attempting to play ${type} audio:`, {
      hasAudioElement: !!audio,
      audioElementHasSrc: !!audio?.src,
      audioElementSrc: audio?.src,
      isPlaying: audioPlayingRef.current[type],
      readyState: audio?.readyState,
    });

    // Only play if audio element exists AND has a valid src
    if (audio && audio.src) {
      // Check if already playing
      if (!audio.paused && audioPlayingRef.current[type]) {
        console.log(`[AUDIO] ${type} is already playing, skipping`);
        return true;
      }

      try {
        // Reset audio if it was at the end
        if (audio.ended) {
          audio.currentTime = 0;
        }

        audio.volume = audioVolume[type];
        await audio.play();
        setAudioPlaying((prev) => ({ ...prev, [type]: true }));
        return true;
      } catch (err) {
        console.error(`[AUDIO] Failed to play ${type} audio:`, err);
        // Only show error message if it's a real error, not user interaction issue
        if (err.name !== "NotAllowedError" && err.name !== "AbortError") {
          message.error(`Failed to play ${type} audio`);
        }
        return false;
      }
    } else {
      return false;
    }
  };
  const pauseAudio = (type) => {
    const audio = audioRefs[type].current;
    if (audio) {
      try {
        audio.pause();
        setAudioPlaying((prev) => ({ ...prev, [type]: false }));
      } catch (err) {
        console.warn(`Error pausing ${type} audio:`, err);
      }
    }
  };
  const stopAllAudio = async () => {
    // Create an array of promises for all audio stops
    const stopPromises = Object.keys(audioRefs).map((type) => {
      return new Promise((resolve) => {
        const audio = audioRefs[type].current;
        if (audio && !audio.paused) {
          try {
            audio.pause();
            audio.currentTime = 0;
            // Clear event listeners that might trigger on load
            audio.oncanplay = null;
            audio.oncanplaythrough = null;
          } catch (err) {
            console.warn(`Error stopping ${type} audio:`, err);
          }
        }
        resolve();
      });
    });

    // Wait for all audio to stop
    await Promise.all(stopPromises);

    // Update state
    setAudioPlaying({
      pre_hype: false,
      warmup: false,
      workout: false,
      preview: false,
    });

    // Longer delay for state updates to propagate and audio elements to fully reset
    await new Promise((resolve) => setTimeout(resolve, 250));
  };
  const handleVolumeChange = (type, volume) => {
    setAudioVolume((prev) => ({ ...prev, [type]: volume }));
    const audio = audioRefs[type].current;
    if (audio) {
      audio.volume = volume;
    }
  };
  // Calculate and update total duration for header
  useEffect(() => {
    const totalDuration = calculateTotalDuration(currentDay);
    window.currentDayTotalDuration = totalDuration;
    localStorage.setItem("currentDayTotalDuration", totalDuration.toString());
    // Trigger a custom event to notify header
    window.dispatchEvent(
      new CustomEvent("totalDurationChanged", { detail: totalDuration }),
    );
  }, [currentDay, activeDay]);
  // Duration Calculations - UPDATED FOR MINUTES AND SECONDS
  const calculateBlockDuration = (blockDuration) => {
    // Convert block duration string "MM:SS" to total seconds
    if (typeof blockDuration === "string" && blockDuration.includes(":")) {
      const [minutes, seconds] = blockDuration.split(":").map(Number);
      return minutes * 60 + (seconds || 0);
    }
    // If it's just a number (for backward compatibility)
    return parseInt(blockDuration) * 60 || 0;
  };

  const calculateTotalDuration = (dayData) => {
    let totalSeconds = 0;
    Object.keys(dayData.data || {}).forEach((blockKey) => {
      if (blockKey.endsWith("_BLOCK") && dayData.data[blockKey]) {
        totalSeconds += calculateBlockDuration(
          dayData.data[blockKey].duration || "0:00",
        );
      }
    });
    return totalSeconds;
  };

  // Helper function to format seconds to MM:SS
  const formatDuration = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Helper function to parse duration string to total seconds
  const parseDuration = (durationStr) => {
    if (!durationStr) return 0;
    if (typeof durationStr === "string") {
      const [minutes, seconds] = durationStr.split(":").map(Number);
      return minutes * 60 + (seconds || 0);
    }
    return durationStr * 60; // For backward compatibility
  };
  // Upload Media
  const uploadMedia = async (file, category) => {
    if (!file) return;
    if (category.includes("_audio")) {
      const validAudioExtensions = [
        ".mp3",
        ".wav",
        ".ogg",
        ".m4a",
        ".aac",
        ".webm",
      ];
      const fileExtension = file.name
        .slice(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!validAudioExtensions.includes(fileExtension)) {
        message.error(
          `Please upload audio in a supported format: ${validAudioExtensions.join(", ")}`,
        );
        return;
      }
    }

    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", category);
    formData.append("day", activeDay);
    setUploading((prev) => ({ ...prev, [category]: true }));

    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.uploadMedia}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // Note: Don't set Content-Type for FormData, let browser set it
        },
        body: formData,
      });

      const data = await res.json();

      if (data.status === 200) {
        const successMessage = data.data?.supports_hls
          ? `${category.replace(/_/g, " ")} uploaded and processed for streaming!`
          : `${category.replace(/_/g, " ")} uploaded successfully!`;

        message.success(successMessage);

        // Update state with new media URL
        setFullWeekData((prev) => {
          const newData = JSON.parse(JSON.stringify(prev));
          if (!newData.full_week[activeDay].media) {
            newData.full_week[activeDay].media = {};
          }
          newData.full_week[activeDay].media[category] = data.data.media_url;
          return newData;
        });
      } else {
        message.error(data.message || "Upload failed");
      }
    } catch (err) {
      message.error("Upload failed - network error");
    } finally {
      setUploading((prev) => ({ ...prev, [category]: false }));
    }
  };
  const handleFileSelect = (e, category) => {
    const file = e.target.files[0];
    if (file) uploadMedia(file, category);
    e.target.value = null;
  };
  // Handle image selection change
  const handleImageChange = (imageName) => {
    autoSave({ new_image: imageName });
  };
  // Get the current image path for preview
  const getCurrentImagePath = () => {
    if (!currentDay.image) return null;
    const selectedImage = availableImages.find(
      (img) => img.value === currentDay.image,
    );
    return selectedImage ? selectedImage.path : null;
  };
  // Live Control: Toggle Stream
  const toggleStream = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      message.error("Live sync not ready");
      return;
    }
    const type = isTableMode ? "show_hero" : "show_table";
    socket.send(JSON.stringify({ type, day: activeDay }));
    setIsTableMode(!isTableMode);
    message.success(isTableMode ? "Switched to Hero" : "Workout Table ON");
  };
  const getTodayName = () => {
    const now = new Date();
    const options = {
      timeZone: "Australia/Sydney",
      weekday: "long",
    };

    // Get the day name in Sydney timezone
    const todayInSydney = now.toLocaleString("en-US", options).toUpperCase();
    return todayInSydney;
  };
  // Open Add Block Modal
  const openAddBlockModal = () => {
    // Find next available block name
    const existingBlocks = Object.keys(currentDay.data || {}).filter((key) =>
      key.endsWith("_BLOCK"),
    );
    const blockLetters = existingBlocks.map((block) => block.charAt(0)).sort();

    // Find next available letter (A, B, C, D, E...)
    let nextLetter = "A";
    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i); // A=65, B=66, etc.
      if (!blockLetters.includes(letter)) {
        nextLetter = letter;
        break;
      }
    }

    const suggestedName = `${nextLetter}_BLOCK`;
    setNewBlockName(suggestedName);
    setNewBlockTitle(suggestedName.replace("_", " "));
    setShowAddBlockModal(true);
  };
  // Add New Block
  const addNewBlock = async () => {
    if (!newBlockName.endsWith("_BLOCK")) {
      message.error("Block name must end with '_BLOCK'");
      return;
    }

    if (!newBlockTitle.trim()) {
      message.error("Block title is required");
      return;
    }

    const token = localStorage.getItem("accessToken");
    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.addBlock}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          day: activeDay,
          block_name: newBlockName,
          block_title: newBlockTitle,
        }),
      });

      const data = await res.json();
      if (data.status === 200) {
        message.success(`${newBlockName} added successfully!`);
        setShowAddBlockModal(false);
        setNewBlockName("");
        setNewBlockTitle("");
        fetchWeeklyPlan(); // Refresh data
      } else {
        message.error(data.error || "Failed to add block");
      }
    } catch (err) {
      message.error("Failed to add block");
    }
  };
  // Delete Block
  const deleteBlock = async (blockKey) => {
    setShowDeleteModal(true);
    setBlockToDelete(blockKey);
  };
  const confirmDeleteBlock = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const res = await fetch(`${apiPath.baseURL}${apiPath.deleteBlock}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          day: activeDay,
          block_name: blockToDelete,
        }),
      });

      const data = await res.json();
      if (data.status === 200) {
        message.success(`${blockToDelete} deleted successfully!`);
        fetchWeeklyPlan(); // Refresh data
      } else {
        message.error(data.error || "Failed to delete block");
      }
    } catch (err) {
      message.error("Failed to delete block");
    } finally {
      setShowDeleteModal(false);
      setBlockToDelete("");
    }
  };
  // Fallback clipboard copy for HTTP/older browsers
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        message.success("Link copied!");
      } else {
        message.error("Copy failed");
      }
    } catch (err) {
      message.error("Copy not supported");
    }

    document.body.removeChild(textArea);
  };
  // Session Header - Updated with audio controls
  const SessionHeader = () => {
    const [title, setTitle] = useState(currentDay.name || "");
    const [selectedColor, setSelectedColor] = useState("#000000ff");
    const [tempColor, setTempColor] = useState(selectedColor);
    useEffect(() => setTitle(currentDay.name || ""), [currentDay]);
    useEffect(() => {
      // keep picker in sync when currentDay loads/changes
      setSelectedColor(currentDay?.color || "#00B5AD");
    }, [currentDay?.color, activeDay]);

    // Listen to WebSocket for workout state updates only
    useEffect(() => {
      if (!socket) return;
      const handler = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.day !== activeDay) return;

          // Only update workout active state, not button modes
          if (
            data.type === "workout_complete" ||
            data.type === "workout_stopped"
          ) {
            setIsWorkoutActive(false);
            setIsWorkoutPaused(false);
            setCurrentMode("hero");
            // Stop workout audio when workout completes
            pauseAudio("workout");
          } else if (
            data.type === "show_countdown" ||
            data.type === "timer_update" ||
            data.type === "show_transition"
          ) {
            setIsWorkoutActive(true);
            setIsWorkoutPaused(false);
            setCurrentMode("workout");
            // Play workout audio when workout starts (after countdown)
            const currentMedia =
              fullWeekDataRef.current?.full_week?.[activeDay]?.media || {};
            if (data.type === "timer_update" && currentMedia.workout_audio) {
              const workoutEl = audioRefs.workout.current;
              // Use ref values instead of closure values to avoid stale state
              if (
                workoutEl &&
                workoutEl.paused &&
                isWorkoutActiveRef.current &&
                !isWorkoutPausedRef.current &&
                !audioPlayingRef.current.workout
              ) {
                playAudio("workout").catch((err) =>
                  console.warn("Auto-play failed:", err),
                );
              }
            }
          } else if (
            data.type === "pause_workout" ||
            data.type === "workout_paused"
          ) {
            setIsWorkoutPaused(true);
            // Pause workout audio when workout is paused
            pauseAudio("workout");
          } else if (
            data.type === "resume_workout" ||
            data.type === "workout_resumed"
          ) {
            setIsWorkoutPaused(false);
            // Resume workout audio when workout is resumed
            const currentMedia =
              fullWeekDataRef.current?.full_week?.[activeDay]?.media || {};
            if (currentMedia.workout_audio) {
              const workoutEl = audioRefs.workout.current;
              if (
                workoutEl &&
                workoutEl.paused &&
                !audioPlayingRef.current.workout
              ) {
                playAudio("workout").catch((err) =>
                  console.warn("Resume audio failed:", err),
                );
              }
            }
          }
        } catch (err) {
          console.error("[WEBSOCKET] Error handling message:", err);
        }
      };
      socket.addEventListener("message", handler);
      return () => socket.removeEventListener("message", handler);
    }, [socket, activeDay]);

    const sendCommand = (type, payload = {}) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        message.error("Live sync not ready");
        return false;
      }

      // Add default payload if empty
      if (Object.keys(payload).length === 0) {
        payload = {
          message: `Command ${type} for ${activeDay}`,
          completion_settings: {
            message:
              currentDay.completion_settings?.message ||
              "You've just completed!",
            top_message:
              currentDay.completion_settings?.top_message || "Great job!",
            workout_transition_message:
              currentDay.completion_settings?.workout_transition_message || "",
          },
        };
      }

      socket.send(JSON.stringify({ type, day: activeDay, payload }));
      return true;
    };

    const handleStartStopWorkout = async () => {
      if (isTransitioning) {
        return;
      }

      setIsTransitioning(true);

      try {
        // Get all blocks for current day
        const allBlocks = Object.keys(currentDay.data || {})
          .filter((key) => key.endsWith("_BLOCK"))
          .sort();
        const completionMsg =
          currentDay.completion_settings?.message || "You've just completed!";
        // Calculate repetitions based on number of blocks
        const repetitions = allBlocks.length;

        // Stop other audio first and wait for cleanup
        if (audioPlayingRef.current.warmup) pauseAudio("warmup");
        if (audioPlayingRef.current.pre_hype) pauseAudio("pre_hype");
        if (audioPlayingRef.current.preview) pauseAudio("preview");
        await new Promise((resolve) => setTimeout(resolve, 150));

        if (isWorkoutActive) {
          if (isWorkoutPaused) {
            // Resume all blocks from current state
            allBlocks.forEach((blockKey, index) => {
              const blockData = currentDay.data[blockKey] || {};
              const duration = parseDuration(blockData.duration) || 600; // 10 minutes default
              const timeSegments = blockData.timeSegments || [];

              setLocalCommandSent("resume_workout");
              sendCommand("resume_workout", {
                day: activeDay,
                resume_all: true,
                current_block: blockKey,
                current_block_index: index,
                all_blocks: [
                  {
                    block_name: blockKey,
                    duration: duration,
                    timeSegments: timeSegments,
                    total_seconds: duration,
                  },
                ],
              });
            });

            setIsWorkoutPaused(false);
            message.success("All Blocks Resumed");
            await new Promise((resolve) => setTimeout(resolve, 150));

            const currentMedia =
              fullWeekData?.full_week?.[activeDay]?.media || {};
            if (
              currentMedia.workout_audio &&
              !audioPlayingRef.current.workout
            ) {
              const success = await playAudio("workout");
              if (!success) {
                console.warn("[WORKOUT] Failed to resume workout audio");
              }
            }
          } else {
            // Pause all blocks
            setLocalCommandSent("pause_workout");
            sendCommand("pause_workout", {
              day: activeDay,
              pause_all: true,
            });
            setIsWorkoutPaused(true);
            message.info("All Blocks Paused");

            pauseAudio("workout");
          }
        } else {
          // Start workout for EACH block with proper data
          allBlocks.forEach((blockKey, index) => {
            const blockData = currentDay.data[blockKey] || {};
            const duration = parseDuration(blockData.duration) || 600;
            const timeSegments = blockData.timeSegments || [];
            const blockTransition = blockData.block_transition || 0;

            // Prepare data for ALL blocks
            const allBlocksData = allBlocks.map((key, idx) => {
              const data = currentDay.data[key] || {};
              return {
                block_name: key,
                duration: parseDuration(data.duration) || 600,
                timeSegments: data.timeSegments || [],
                total_seconds: parseDuration(data.duration) || 600,
                block_transition: data.block_transition || 0,
              };
            });

            // Check if we should resume existing workout
            const shouldResume = isWorkoutPaused;
            const dayColor = currentDay.color || "#00B5AD";

            sendCommand("start_workout", {
              day: activeDay,
              all_blocks: allBlocksData, // Send data for ALL blocks
              block_transition: blockTransition,
              current_block: blockKey,
              current_block_index: index,
              total_blocks: allBlocks.length,
              current_repetition: 1,
              max_repetitions: repetitions, // Dynamic: based on number of blocks
              repetition_count: 1,
              resume_existing: shouldResume,
              day_colors: dayColor,
              completion_message: completionMsg,
              completion_top_message: completionTopMessage,
              workout_transition_message: workoutTransitionMessage,
            });
          });

          setIsWorkoutActive(true);
          setIsWorkoutPaused(false);
          message.success(
            `Workout Started! (${repetitions} repetitions for each of ${allBlocks.length} blocks)`,
          );

          // Stop other audio and play workout audio
          await stopOtherAudio("workout");

          // Wait for audio cleanup before starting new audio
          await new Promise((resolve) => setTimeout(resolve, 200));

          const currentMedia =
            fullWeekData?.full_week?.[activeDay]?.media || {};
          if (currentMedia.workout_audio) {
            console.log("[WORKOUT] Starting workout: playing workout audio");
            const success = await playAudio("workout");
            if (!success) {
              console.warn("[WORKOUT] Failed to start workout audio");
            }
          }
        }
      } finally {
        setIsTransitioning(false);
      }
    };

    const handlePreview = async () => {
      if (isTransitioning) {
        return;
      }

      setIsTransitioning(true);

      try {
        if (currentModeRef.current === "preview") {
          setCurrentMode("hero");
          sendCommand("show_hero");
          pauseAudio("preview");
          message.success("PREVIEW STOPPED");
        } else {
          const videoUrl = currentDay.media?.preview_video;
          const audioUrl = currentDay.media?.preview_audio;

          if (audioPlayingRef.current.workout) pauseAudio("workout");

          // Stop all OTHER audio (not preview)
          if (audioPlayingRef.current.pre_hype) pauseAudio("pre_hype");
          if (audioPlayingRef.current.warmup) pauseAudio("warmup");

          await new Promise((resolve) => setTimeout(resolve, 100));

          setCurrentMode("preview");
          // Send preview command - stream page will show video if available, or table if not
          setLocalCommandSent("show_preview");
          sendCommand("show_preview", { video_url: videoUrl });

          if (audioUrl && !audioPlayingRef.current.preview) {
            await playAudio("preview");
          }

          message.success("PREVIEW LIVE!");
        }
      } finally {
        setIsTransitioning(false);
      }
    };

    const handlePreHype = async () => {
      if (isTransitioning) {
        return;
      }

      setIsTransitioning(true);

      try {
        if (currentModeRef.current === "pre_hype") {
          setLocalCommandSent("show_hero");
          setCurrentMode("hero");
          sendCommand("show_hero");
          pauseAudio("pre_hype");
          message.success("PRE-HYPE STOPPED");
        } else {
          const videoUrl = currentDay.media?.pre_hype_video;
          const audioUrl = currentDay.media?.pre_hype_audio;

          if (!videoUrl && !audioUrl) {
            message.warning("Please upload Pre-Hype Video or Audio first!");
            return;
          }

          // Stop workout completely before starting pre-hype
          setIsWorkoutActive(false);
          setIsWorkoutPaused(false);
          await stopAllAudio();

          // Wait for cleanup to complete
          await new Promise((resolve) => setTimeout(resolve, 100));

          setCurrentMode("pre_hype");

          // ALWAYS set localCommandSent to prevent WebSocket echo
          setLocalCommandSent("show_pre_hype");

          if (videoUrl) {
            sendCommand("show_pre_hype", { video_url: videoUrl });
          } else {
            // Send command even without video to sync stream page
            sendCommand("show_pre_hype");
          }

          if (audioUrl) {
            const success = await playAudio("pre_hype");
            if (success) {
              message.success("PRE-HYPE LIVE!");
            } else {
              message.warning("PRE-HYPE LIVE! (Audio failed to start)");
            }
          } else {
            message.success("PRE-HYPE LIVE!");
          }
        }
      } finally {
        setIsTransitioning(false);
      }
    };

    const handleWarmup = async () => {
      if (isTransitioning) {
        return;
      }

      setIsTransitioning(true);

      try {
        if (currentModeRef.current === "warmup") {
          setLocalCommandSent("show_hero");
          setCurrentMode("hero");
          sendCommand("show_hero");
          pauseAudio("warmup");
          message.success("WARM-UP STOPPED");
        } else {
          const videoUrl = currentDay.media?.warmup_video;
          const audioUrl = currentDay.media?.warmup_audio;

          if (!videoUrl && !audioUrl) {
            message.warning("Please upload Warm-Up Video or Audio first!");
            return;
          }

          // Stop workout completely before starting warmup
          setIsWorkoutActive(false);
          setIsWorkoutPaused(false);
          await stopAllAudio();

          // Wait for cleanup to complete
          await new Promise((resolve) => setTimeout(resolve, 100));

          setCurrentMode("warmup");

          // ALWAYS set localCommandSent to prevent WebSocket echo
          setLocalCommandSent("show_warmup");

          if (videoUrl) {
            sendCommand("show_warmup", { video_url: videoUrl });
          } else {
            // Send command even without video to sync stream page
            sendCommand("show_warmup");
          }

          if (audioUrl) {
            const success = await playAudio("warmup");
            if (success) {
              message.success("WARM-UP LIVE!");
            } else {
              message.warning("WARM-UP LIVE! (Audio failed to start)");
            }
          } else {
            message.success("WARM-UP LIVE!");
          }
        }
      } finally {
        setIsTransitioning(false);
      }
    };
    const stopAllMedia = async () => {
      await stopAllAudio();
      setCurrentMode("hero");
      setIsWorkoutActive(false);
      setIsWorkoutPaused(false);
    };
    const handleSync = async () => {
      if (isTransitioning) {
        return;
      }

      setIsTransitioning(true);

      try {
        console.log("[SYNC] Syncing all clients to HERO mode");
        // Sync all connected clients to current state
        setCurrentMode("hero");
        sendCommand("show_hero");
        await stopAllAudio();
        message.success("All clients synced to HERO mode");
      } finally {
        setIsTransitioning(false);
      }
    };
    const handleRestart = async () => {
      if (isTransitioning) {
        return;
      }

      setIsTransitioning(true);

      try {
        console.log("[RESTART] Restarting all streams");

        // Stop all active workouts first
        sendCommand("stop_workout", {
          day: activeDay,
          stop_all: true,
        });

        // Reset all streams to initial hero state
        setCurrentMode("hero");
        setIsWorkoutActive(false);
        setIsWorkoutPaused(false);

        // Send hero command for all blocks
        sendCommand("show_hero", {
          day: activeDay,
          reset_all: true,
        });

        // Stop all audio
        await stopAllAudio();

        message.success("All streams restarted to initial state");
      } finally {
        setIsTransitioning(false);
      }
    };

    return (
      <div className="session-header">
        <div className="days-navigation">
          {DAYS.map((day) => (
            <button
              key={day}
              className={`day-button ${day === activeDay ? "active" : ""} ${day === getTodayName() ? "today-highlight" : ""}`}
              onClick={() => !isDayTransitioning && setActiveDay(day)}
              disabled={isDayTransitioning}
              style={{
                cursor: isDayTransitioning ? "wait" : "pointer",
                opacity: isDayTransitioning && day !== activeDay ? 0.5 : 1,
              }}
            >
              {day.slice(0, 10)}
              {day === getTodayName() && " (TODAY)"}
              {isDayTransitioning && day === activeDay}
            </button>
          ))}
        </div>
        <div className="session-info">
          <div className="session-title-group">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() =>
                title !== currentDay.name && autoSave({ new_title: title })
              }
              style={{
                fontSize: 32,
                fontWeight: "bold",
                color: "#00B5AD",
                background: "transparent",
                border: "2px solid transparent",
                borderRadius: 12,
                padding: "8px 16px",
                minWidth: 250,
              }}
              placeholder="Workout Name"
            />
          </div>
          {/* ====== COLOR PICKER ====== */}
          {/* <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => setTempColor(e.target.value)} // temporary color store
                style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid #333", cursor: "pointer" }}
                title="Select theme color for this day"
              />
              <button
                onClick={() => handleColorChange(tempColor)}
                style={{
                  padding: "6px 12px",
                  background: "#444",
                  border: "1px solid #666",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: "600"
                }}
              >
                Apply
              </button>
              <div style={{ color: "#ccc", fontWeight: "600" }}>Theme Color</div>
            </div> */}

          {/* ACTION BUTTONS */}
          <div
            className="session-actions"
            style={{
              gap: 16,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {/* 1. PREVIEW/RESET */}
            <button
              className="action-button action-button_preview"
              style={{
                width: "120px",
                opacity: isTransitioning ? 0.6 : 1,
                cursor: isTransitioning ? "not-allowed" : "pointer",
              }}
              onClick={handlePreview}
              disabled={isTransitioning}
            >
              {currentMode === "preview" ? "RESET" : "PREVIEW"}
              {media.preview_audio && (
                <span style={{ marginLeft: 8 }}>
                  {audioPlaying.preview ? (
                    <Volume2 size={16} />
                  ) : (
                    <VolumeX size={16} />
                  )}
                </span>
              )}
            </button>

            {/* RESTART BUTTON */}
            <button
              className="action-button"
              onClick={handleRestart}
              disabled={isTransitioning}
              style={{
                width: "120px",
                opacity: isTransitioning ? 0.6 : 1,
                cursor: isTransitioning ? "not-allowed" : "pointer",
              }}
            >
              RESTART
            </button>

            {/* 2. PRE-HYPE */}
            <button
              className="action-button action-button_pre-hype"
              onClick={handlePreHype}
              disabled={isTransitioning}
              style={{
                width: "130px",
                padding: "5px",
                opacity: isTransitioning ? 0.6 : 1,
                cursor: isTransitioning ? "not-allowed" : "pointer",
              }}
            >
              {currentMode === "pre_hype" ? "STOP" : "PRE-HYPE"}
              {media.pre_hype_audio && (
                <span style={{ marginLeft: 8 }}>
                  {audioPlaying.pre_hype ? (
                    <Volume2 size={16} />
                  ) : (
                    <VolumeX size={16} />
                  )}
                </span>
              )}
            </button>

            {/* 3. WARM-UP */}
            <button
              className="action-button action-button_warm_up"
              onClick={handleWarmup}
              disabled={isTransitioning}
              style={{
                width: "130px",
                padding: "5px",
                opacity: isTransitioning ? 0.6 : 1,
                cursor: isTransitioning ? "not-allowed" : "pointer",
              }}
            >
              {currentMode === "warmup" ? "STOP" : "WARM-UP"}
              {media.warmup_audio && (
                <span style={{ marginLeft: 8 }}>
                  {audioPlaying.warmup ? (
                    <Volume2 size={16} />
                  ) : (
                    <VolumeX size={16} />
                  )}
                </span>
              )}
            </button>

            {/* 4. START/PAUSE/RESUME ALL BLOCKS */}
            <button
              className="action-button"
              onClick={handleStartStopWorkout}
              disabled={isTransitioning}
              style={{
                width: "100px",
                opacity: isTransitioning ? 0.6 : 1,
                cursor: isTransitioning ? "not-allowed" : "pointer",
              }}
            >
              {isWorkoutActive ? "STOP" : "START"}
              {media.workout_audio && isWorkoutActive && !isWorkoutPaused && (
                <span style={{ marginLeft: 8 }}>
                  {audioPlaying.workout ? (
                    <Volume2 size={16} />
                  ) : (
                    <VolumeX size={16} />
                  )}
                </span>
              )}
            </button>

            {/* 5. SYNC ================ */}
            <button
              className="action-button"
              onClick={handleSync}
              disabled={isTransitioning}
              style={{
                width: "95px",
                opacity: isTransitioning ? 0.6 : 1,
                cursor: isTransitioning ? "not-allowed" : "pointer",
              }}
            >
              SYNC
            </button>
          </div>
        </div>
      </div>
    );
  };
  // Workout Block
  const WorkoutBlock = ({ block, blockKey }) => {
    const [exercises, setExercises] = useState(
      (block.tasks || []).map((t, i) => ({
        id: t.id || `${blockKey}-${i}`,
        name: t.name || "",
        sets: t.sets || "",
        reps: t.reps || "",
        tempo: t.tempo || "",
      })),
    );
    // Initialize duration as MM:SS format
    const [blockDuration, setBlockDuration] = useState(() => {
      const duration = block.duration;
      if (typeof duration === "string" && duration.includes(":")) {
        return duration; // Already in MM:SS format
      } else if (duration) {
        // Convert number of minutes to MM:SS format
        const minutes = parseInt(duration) || 40;
        return `${minutes}:00`;
      }
      return "40:00"; // Default
    });
    const [notes, setNotes] = useState(block.cooldown || "");
    const [showDurationModal, setShowDurationModal] = useState(false);
    const [tempDurationMinutes, setTempDurationMinutes] = useState("40");
    const [tempDurationSeconds, setTempDurationSeconds] = useState("00");
    const [blockTransition, setBlockTransition] = useState(
      block.block_transition || 0,
    );
    const [timeSegments, setTimeSegments] = useState(() => {
      // If backend has timeSegments, use them, otherwise create default
      if (block.timeSegments && block.timeSegments.length > 0) {
        // Ensure backward compatibility - convert old format to new format
        return block.timeSegments.map((segment) => {
          if (segment.workingTime !== undefined) {
            return segment; // New format
          } else {
            // Old format - convert duration to workingTime
            return {
              workingTime: segment.duration || 2400, // 40 minutes default
              restTime: 0,
              duration: segment.duration || 2400,
              label: segment.label || "40:00",
            };
          }
        });
      }
      const defaultDuration = parseDuration(blockDuration);
      return [
        {
          workingTime: defaultDuration,
          restTime: 0,
          duration: defaultDuration,
          label: formatDuration(defaultDuration),
        },
      ];
    });
    const [showSegmentModal, setShowSegmentModal] = useState(false);
    const [currentSegments, setCurrentSegments] = useState([]);
    const [blockName, setBlockName] = useState(
      block.blockName || block.name || "EXERCISE",
    );

    // NEW: Use refs for debouncing to prevent multiple API calls
    const saveTimeoutRef = useRef(null);
    const lastSaveDataRef = useRef(null);

    // Debug log and update blockName when block changes
    useEffect(() => {
      const newBlockName = block.blockName || block.name || "EXERCISE";
      if (newBlockName !== blockName) {
        setBlockName(newBlockName);
      }
    }, [block.blockName, block.name]);

    // NEW: Improved debounce save function
    const debounceSave = (updatedExercises, duration, segments, notesValue) => {
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Prepare save data
      const saveData = {
        block: blockKey,
        block_data: {
          title: block.title,
          blockName: blockName,
          duration: duration || blockDuration,
          timeSegments: segments || timeSegments,
          cooldown: notesValue || notes,
          tasks: (updatedExercises || exercises).map((ex) => ({ ...ex })),
          block_transition: blockTransition || 0,
        },
      };

      // Store for comparison
      lastSaveDataRef.current = JSON.stringify(saveData);

      // Set new timeout with longer delay (5000ms)
      saveTimeoutRef.current = setTimeout(() => {
        autoSave(saveData);
      }, 5000);
    };

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    }, []);

    const handleChange = (id, field, value) => {
      const updated = exercises.map((ex) =>
        ex.id === id ? { ...ex, [field]: value } : ex,
      );
      setExercises(updated);
      debounceSave(updated, blockDuration, timeSegments, notes);
    };

    const handleDurationChange = (value) => {
      // Validate and format duration input
      let formattedValue = value;

      // Allow only numbers and colon
      if (!/^[\d:]*$/.test(value)) return;

      // Handle manual editing of duration
      if (value.includes(":")) {
        const [minutes, seconds] = value.split(":");
        const mins = parseInt(minutes) || 0;
        const secs = parseInt(seconds) || 0;

        // Ensure seconds are between 0-59
        const validSecs = Math.min(59, Math.max(0, secs));

        formattedValue = `${mins}:${validSecs.toString().padStart(2, "0")}`;
      } else {
        // If no colon, treat as minutes
        const minutes = parseInt(value) || 40;
        formattedValue = `${minutes}:00`;
      }

      setBlockDuration(formattedValue);

      // Reset segments when duration changes directly
      const newSegments = [
        {
          workingTime: parseDuration(formattedValue),
          restTime: 0,
          duration: parseDuration(formattedValue),
          label: formattedValue,
        },
      ];
      setTimeSegments(newSegments);
      debounceSave(exercises, formattedValue, newSegments, notes);
    };

    const openDurationModal = () => {
      const [minutes, seconds] = blockDuration.split(":");
      setTempDurationMinutes(minutes);
      setTempDurationSeconds(seconds);
      setShowDurationModal(true);
    };

    const saveDuration = () => {
      const oldDuration = blockDuration;
      const newDuration = `${parseInt(tempDurationMinutes) || 0}:${(parseInt(tempDurationSeconds) || 0).toString().padStart(2, "0")}`;

      // Reset to single segment when duration changes via modal
      const newSegments = [
        {
          workingTime: parseDuration(newDuration),
          restTime: 0,
          duration: parseDuration(newDuration),
          label: newDuration,
        },
      ];
      setTimeSegments(newSegments);
      setBlockDuration(newDuration);

      debounceSave(exercises, newDuration, newSegments, notes);
      setShowDurationModal(false);
      message.success(`${blockKey} duration: ${oldDuration} → ${newDuration}`);
    };

    const openSegmentModal = () => {
      setCurrentSegments([...timeSegments]);
      setShowSegmentModal(true);
    };

    const addSegment = () => {
      setCurrentSegments([
        ...currentSegments,
        { workingTime: 60, restTime: 0, duration: 60, label: "01:00" },
      ]);
    };

    const updateSegment = (index, type, minutes, seconds) => {
      const totalSeconds = minutes * 60 + seconds;

      const updated = [...currentSegments];
      if (!updated[index]) {
        updated[index] = { workingTime: 60, restTime: 0, label: "01:00" };
      }

      if (type === "working") {
        updated[index].workingTime = totalSeconds;
      } else {
        updated[index].restTime = totalSeconds;
      }

      // Create better label that shows both working and rest time
      const workingMinutes = Math.floor(updated[index].workingTime / 60);
      const workingSeconds = updated[index].workingTime % 60;
      const restMinutes = Math.floor(updated[index].restTime / 60);
      const restSeconds = updated[index].restTime % 60;

      let label = "";
      if (workingMinutes > 0 || workingSeconds > 0) {
        label += `${workingMinutes.toString().padStart(2, "0")}:${workingSeconds.toString().padStart(2, "0")}`;
      }

      if (updated[index].restTime > 0) {
        label += " + ";
        if (restMinutes > 0 || restSeconds > 0) {
          label += `${restMinutes.toString().padStart(2, "0")}:${restSeconds.toString().padStart(2, "0")}`;
        }
        label += "R";
      }

      updated[index].label = label || "01:00";

      // Total duration is working + rest time
      updated[index].duration =
        updated[index].workingTime + updated[index].restTime;

      setCurrentSegments(updated);
    };

    const removeSegment = (index) => {
      setCurrentSegments(currentSegments.filter((_, i) => i !== index));
    };

    // NEW: Function to duplicate a specific segment
    const duplicateSegment = (index) => {
      const segmentToDuplicate = currentSegments[index];
      const newSegment = {
        workingTime: segmentToDuplicate.workingTime,
        restTime: segmentToDuplicate.restTime,
        duration: segmentToDuplicate.duration,
        label: segmentToDuplicate.label,
      };
      const newSegments = [
        ...currentSegments.slice(0, index + 1),
        newSegment,
        ...currentSegments.slice(index + 1),
      ];
      setCurrentSegments(newSegments);
    };

    const saveSegments = () => {
      // Validate working time only
      const invalidSegments = currentSegments.filter(
        (seg) => !seg.workingTime || seg.workingTime <= 0,
      );

      if (invalidSegments.length > 0) {
        message.error("All segments must have working time greater than 0");
        return;
      }

      // ✅ Calculate total time from segments (working + rest)
      const totalSegmentSeconds = currentSegments.reduce((sum, seg) => {
        return sum + (seg.workingTime || 0) + (seg.restTime || 0);
      }, 0);

      // ✅ Convert to MM:SS
      const newBlockDuration = formatDuration(totalSegmentSeconds);

      // ✅ Update both segments & duration
      setTimeSegments([...currentSegments]);
      setBlockDuration(newBlockDuration);

      setShowSegmentModal(false);

      // ✅ Save using new duration
      debounceSave(exercises, newBlockDuration, currentSegments, notes);

      message.success(`Block duration auto-set to ${newBlockDuration}`);
    };

    const handleAdd = () => {
      const newEx = {
        id: Date.now().toString(),
        name: "",
        sets: "",
        reps: "",
        tempo: "",
      };
      const updated = [...exercises, newEx];
      setExercises(updated);
      debounceSave(updated, blockDuration, timeSegments, notes);
    };

    const handleDelete = (id) => {
      const updated = exercises.filter((ex) => ex.id !== id);
      setExercises(updated);
      debounceSave(updated, blockDuration, timeSegments, notes);
    };

    // NEW: Block name change handler with debouncing
    const handleBlockNameChange = (e) => {
      const newName = e.target.value;
      setBlockName(newName);

      // Debounce the save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        autoSave({
          block: blockKey,
          block_data: {
            title: block.title,
            blockName: newName,
            duration: blockDuration,
            timeSegments: timeSegments,
            cooldown: notes,
            tasks: exercises.map((ex) => ({ ...ex })),
            block_transition: blockTransition || 0,
          },
        });
      }, 5000);
    };
    // NEW: Notes change handler with debouncing
    const handleNotesChange = (e) => {
      const newNotes = e.target.value;
      setNotes(newNotes);
      debounceSave(exercises, blockDuration, timeSegments, newNotes);
    };
    // NEW: Block transition change handler with debouncing
    const handleBlockTransitionChange = (value) => {
      const newTransition = parseInt(value) || 0;
      setBlockTransition(newTransition);
      debounceSave(exercises, blockDuration, timeSegments, notes);
    };
    return (
      <div className="workout-block">
        <div className="workout-block-header">
          <div>
            <h2 className="workout-block-title">{block.title}</h2>
            <div
              style={{ display: "flex", alignItems: "center", gap: 8 }}
              className="time-count_mainjh"
            >
              <Timer size={20} color="#a8a8a8" />
              <Input
                value={blockDuration}
                onChange={(e) => handleDurationChange(e.target.value)}
                disabled
                style={{
                  width: 100,
                  textAlign: "center",
                  fontWeight: "bold",
                  fontFamily: "monospace",
                  fontSize: "16px",
                }}
                placeholder="MM:SS"
                pattern="[0-9]{1,2}:[0-9]{2}"
              />
              <span
                style={{ fontSize: 18, fontWeight: "bold", color: "#a8a8a8" }}
              ></span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="edit-button"
              onClick={() => {
                const token = localStorage.getItem("accessToken");

                const url = `${window.location.origin}/stream-section?day=${activeDay}&block=${blockKey}&token=${token}`;

                // Try modern clipboard API first
                if (navigator.clipboard && window.isSecureContext) {
                  navigator.clipboard
                    .writeText(url)
                    .then(() => {
                      message.success("Link copied!");
                    })
                    .catch(() => {
                      fallbackCopyTextToClipboard(url);
                    });
                } else {
                  // Fallback for HTTP or older browsers
                  fallbackCopyTextToClipboard(url);
                }
              }}
            >
              <Link size={16} />
            </button>
            <button
              className="edit-button"
              onClick={() => deleteBlock(blockKey)}
              style={{ background: "#e74c3c" }}
              title="Delete Block"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        <div className="workout-content">
          <div className="table-section-table">
            <table className="exercise-table">
              <thead>
                <tr>
                  <th>
                    <Input
                      value={blockName}
                      onChange={handleBlockNameChange}
                      placeholder="EXERCISE"
                    />
                  </th>
                  <th className="center narrow">SETS</th>
                  <th className="center narrow">REPS</th>
                  <th className="center narrow">TEMPO</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {exercises.map((ex) => (
                  <tr key={ex.id}>
                    <td>
                      <Input
                        value={ex.name}
                        onChange={(e) =>
                          handleChange(ex.id, "name", e.target.value)
                        }
                        placeholder="Exercise"
                      />
                    </td>
                    <td className="center">
                      <Input
                        value={ex.sets}
                        onChange={(e) =>
                          handleChange(ex.id, "sets", e.target.value)
                        }
                        style={{ width: 50 }}
                      />
                    </td>
                    <td className="center">
                      <Input
                        value={ex.reps}
                        onChange={(e) =>
                          handleChange(ex.id, "reps", e.target.value)
                        }
                        style={{ width: 60 }}
                      />
                    </td>
                    <td className="center">
                      <Input
                        value={ex.tempo}
                        onChange={(e) =>
                          handleChange(ex.id, "tempo", e.target.value)
                        }
                        style={{ width: 60 }}
                      />
                    </td>
                    <td className="center">
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          className="icon-button"
                          onClick={() => {
                            // Create a duplicate of THIS specific exercise
                            const newExercise = {
                              ...ex, // Use the current exercise 'ex' from the map
                              id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            };

                            // Find the current index to insert after it
                            const currentIndex = exercises.findIndex(
                              (e) => e.id === ex.id,
                            );
                            const updated = [
                              ...exercises.slice(0, currentIndex + 1),
                              newExercise,
                              ...exercises.slice(currentIndex + 1),
                            ];

                            setExercises(updated);
                            debounceSave(
                              updated,
                              blockDuration,
                              timeSegments,
                              notes,
                            );
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#3498db",
                            padding: "4px",
                          }}
                          title="Duplicate"
                        >
                          <Plus size={16} />
                        </button>

                        <button
                          className="icon-button"
                          onClick={() => handleDelete(ex.id)}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#e74c3c",
                            padding: "4px",
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="5" className="add-row">
                    <button className="add-button" onClick={handleAdd}>
                      <Plus size={16} /> Add Exercise
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="progress-bar-container">
              <div className="clock-outline-btn">
                <button
                  onClick={openDurationModal}
                  style={{
                    background: "transparent",
                    border: "2px solid #00B5AD",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    color: "#a8a8a8",
                  }}
                >
                  <Clock size={20} />
                </button>
              </div>
              <div
                className="progress-bar-fill"
                style={{ display: "flex", width: "100%", gap: "5px" }}
              >
                {timeSegments.map((segment, index) => {
                  // Each segment width should include BOTH working + rest time
                  const segmentTotalSeconds =
                    (segment.workingTime || 0) + (segment.restTime || 0);
                  const blockTotalSeconds = parseDuration(blockDuration);
                  const segmentWidth =
                    (segmentTotalSeconds / blockTotalSeconds) * 100;

                  return (
                    <button key={index} onClick={openSegmentModal}>
                      {/* Show working time + rest time in label */}
                      {segment.label}
                      {segment.restTime > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            background: "#FF9800",
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              <div
                className="progress-bar-total"
                style={{
                  color: "#a8a8a8",
                  fontWeight: "bold",
                  fontSize: "16px",
                  padding: "8px",
                  fontFamily: "monospace",
                }}
              >
                {blockDuration}
              </div>
            </div>
            {/* Duration Modal - UPDATED FOR MINUTES AND SECONDS */}
            {showDurationModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    background: "#1a1a2e",
                    padding: "30px",
                    borderRadius: "12px",
                    border: "2px solid #00B5AD",
                    minWidth: "300px",
                    textAlign: "center",
                  }}
                >
                  <h3 style={{ color: "#00B5AD", marginBottom: "20px" }}>
                    Set {blockKey} Duration
                  </h3>
                  <div
                    style={{
                      marginBottom: "10px",
                      color: "#ccc",
                      fontFamily: "monospace",
                      fontSize: "18px",
                    }}
                  >
                    Current: {blockDuration}
                  </div>
                  <div
                    style={{
                      marginBottom: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <label style={{ color: "#ccc", marginBottom: "5px" }}>
                        Minutes
                      </label>
                      <Input
                        type="number"
                        value={tempDurationMinutes}
                        onChange={(e) => setTempDurationMinutes(e.target.value)}
                        style={{
                          width: "80px",
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: "bold",
                          border: "2px solid #00B5AD",
                        }}
                        min={0}
                        max={120}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        color: "#00B5AD",
                        marginTop: "15px",
                      }}
                    >
                      :
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <label style={{ color: "#ccc", marginBottom: "5px" }}>
                        Seconds
                      </label>
                      <Input
                        type="number"
                        value={tempDurationSeconds}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.length > 2) value = value.slice(0, 2);
                          if (parseInt(value) > 59) value = "59";
                          setTempDurationSeconds(value.padStart(2, "0"));
                        }}
                        style={{
                          width: "80px",
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: "bold",
                          border: "2px solid #00B5AD",
                        }}
                        min={0}
                        max={59}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      marginBottom: "15px",
                      color: "#00B5AD",
                      fontSize: "14px",
                      fontFamily: "monospace",
                    }}
                  >
                    New duration will be: {parseInt(tempDurationMinutes) || 0}:
                    {(parseInt(tempDurationSeconds) || 0)
                      .toString()
                      .padStart(2, "0")}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={() => setShowDurationModal(false)}
                      style={{
                        padding: "8px 16px",
                        background: "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveDuration}
                      style={{
                        padding: "8px 16px",
                        background: "#00B5AD",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Time Segmentation Modal */}
            {showSegmentModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1001,
                }}
              >
                <div
                  style={{
                    background: "#1a1a2e",
                    padding: "30px",
                    borderRadius: "12px",
                    border: "2px solid #00B5AD",
                    minWidth: "500px",
                    maxHeight: "80vh",
                    overflowY: "auto",
                  }}
                >
                  <h3 style={{ color: "#00B5AD", marginBottom: "20px" }}>
                    Create Time Segments ({blockDuration} Total)
                  </h3>

                  <div style={{ marginBottom: "20px" }}>
                    {currentSegments.map((segment, index) => {
                      const workingMinutes = Math.floor(
                        (segment.workingTime || 0) / 60,
                      );
                      const workingSeconds = (segment.workingTime || 0) % 60;
                      const restMinutes = Math.floor(
                        (segment.restTime || 0) / 60,
                      );
                      const restSeconds = (segment.restTime || 0) % 60;
                      return (
                        <div
                          key={index}
                          style={{
                            marginBottom: "15px",
                            padding: "15px",
                            background: "#2a2a3e",
                            borderRadius: "8px",
                            border: "1px solid #00B5AD",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "10px",
                            }}
                          >
                            <span
                              style={{
                                color: "#00B5AD",
                                fontWeight: "bold",
                                fontSize: "16px",
                              }}
                            >
                              Segment {index + 1}
                            </span>
                            <div style={{ display: "flex", gap: "5px" }}>
                              <button
                                onClick={() => duplicateSegment(index)}
                                style={{
                                  background: "#3498db",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "5px 10px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                                title="Duplicate this segment"
                              >
                                Duplicate
                              </button>
                              <button
                                onClick={() => removeSegment(index)}
                                style={{
                                  background: "#e74c3c",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "5px 10px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>

                          {/* Working Time */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "10px",
                              padding: "8px",
                              background: "#3a3a5c",
                              borderRadius: "6px",
                              border: "1px solid #00B5AD",
                            }}
                          >
                            <span
                              style={{
                                color: "#00B5AD",
                                fontWeight: "bold",
                                minWidth: "80px",
                              }}
                            >
                              Working:
                            </span>
                            <Input
                              type="number"
                              value={workingMinutes}
                              onChange={(e) =>
                                updateSegment(
                                  index,
                                  "working",
                                  parseInt(e.target.value) || 0,
                                  workingSeconds,
                                )
                              }
                              style={{ width: "60px" }}
                              min={0}
                              max={60}
                            />
                            <span style={{ color: "#ccc" }}>min</span>
                            <Input
                              type="number"
                              value={workingSeconds}
                              onChange={(e) =>
                                updateSegment(
                                  index,
                                  "working",
                                  workingMinutes,
                                  parseInt(e.target.value) || 0,
                                )
                              }
                              style={{ width: "60px" }}
                              min={0}
                              max={59}
                            />
                            <span style={{ color: "#ccc" }}>sec</span>
                          </div>

                          {/* Rest Time */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "8px",
                              background: "#3a3a5c",
                              borderRadius: "6px",
                              border: "1px solid #FF9800",
                            }}
                          >
                            <span
                              style={{
                                color: "#FF9800",
                                fontWeight: "bold",
                                minWidth: "80px",
                              }}
                            >
                              Rest:
                            </span>
                            <Input
                              type="number"
                              value={restMinutes}
                              onChange={(e) =>
                                updateSegment(
                                  index,
                                  "rest",
                                  parseInt(e.target.value) || 0,
                                  restSeconds,
                                )
                              }
                              style={{ width: "60px" }}
                              min={0}
                              max={60}
                            />
                            <span style={{ color: "#ccc" }}>min</span>
                            <Input
                              type="number"
                              value={restSeconds}
                              onChange={(e) =>
                                updateSegment(
                                  index,
                                  "rest",
                                  restMinutes,
                                  parseInt(e.target.value) || 0,
                                )
                              }
                              style={{ width: "60px" }}
                              min={0}
                              max={59}
                            />
                            <span style={{ color: "#ccc" }}>sec</span>
                          </div>
                        </div>
                      );
                    })}

                    <button
                      onClick={addSegment}
                      style={{
                        background: "#3498db",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        marginTop: "10px",
                      }}
                    >
                      + Add Segment
                    </button>
                    {/* Block Transition */}
                    <div
                      style={{
                        marginTop: "20px",
                        padding: "10px",
                        background: "#3a3a5c",
                        borderRadius: "8px",
                        border: "1px solid #00B5AD",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <span
                        style={{
                          color: "#00B5AD",
                          fontWeight: "bold",
                          fontSize: "16px",
                        }}
                      >
                        Block Transition
                      </span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <input
                          type="number"
                          min={0}
                          max={30}
                          value={blockTransition || 0}
                          onChange={(e) =>
                            handleBlockTransitionChange(e.target.value)
                          }
                          style={{
                            width: "80px",
                            padding: "6px 10px",
                            background: "#2c2c45",
                            border: "1px solid #00B5AD",
                            borderRadius: "6px",
                            color: "white",
                          }}
                        />
                        <span style={{ color: "#ccc" }}>seconds</span>
                        <button
                          onClick={() => handleBlockTransitionChange(0)}
                          style={{
                            background: "#e74c3c",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ========== ADD TIME SUMMARY HERE ========== */}
                  <div
                    style={{
                      marginBottom: "15px",
                      padding: "10px",
                      background: "#2a2a3e",
                      borderRadius: "6px",
                      border: "1px solid #00B5AD",
                    }}
                  >
                    <div
                      style={{
                        color: "#00B5AD",
                        fontWeight: "bold",
                        marginBottom: "5px",
                      }}
                    >
                      Time Summary:
                    </div>
                    <div
                      style={{
                        color: "#ccc",
                        fontSize: "14px",
                        fontFamily: "monospace",
                      }}
                    >
                      <div>
                        Total Working:{" "}
                        {formatDuration(
                          currentSegments.reduce(
                            (sum, seg) => sum + (seg.workingTime || 0),
                            0,
                          ),
                        )}
                      </div>
                      <div>
                        Total Rest:{" "}
                        {formatDuration(
                          currentSegments.reduce(
                            (sum, seg) => sum + (seg.restTime || 0),
                            0,
                          ),
                        )}
                      </div>
                      <div>
                        Total Segments:{" "}
                        {formatDuration(
                          currentSegments.reduce(
                            (sum, seg) =>
                              sum +
                              ((seg.workingTime || 0) + (seg.restTime || 0)),
                            0,
                          ),
                        )}{" "}
                        {/* / {blockDuration} */}
                      </div>
                      {/* <div
                          style={{
                            color:
                              currentSegments.reduce(
                                (sum, seg) =>
                                  sum +
                                  ((seg.workingTime || 0) + (seg.restTime || 0)),
                                0,
                              ) > parseDuration(blockDuration)
                                ? "#e74c3c"
                                : "#00B5AD",
                            fontWeight: "bold",
                          }}
                        >
                          Remaining:{" "}
                          {formatDuration(
                            parseDuration(blockDuration) -
                              currentSegments.reduce(
                                (sum, seg) =>
                                  sum +
                                  ((seg.workingTime || 0) + (seg.restTime || 0)),
                                0,
                              ),
                          )}
                        </div> */}
                    </div>
                  </div>
                  {/* ========== END TIME SUMMARY ========== */}

                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      onClick={() => setShowSegmentModal(false)}
                      style={{
                        padding: "8px 16px",
                        background: "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveSegments}
                      style={{
                        padding: "8px 16px",
                        background: "#00B5AD",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Save Segments
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="notes-section">
            <h3 className="notes-title">Notes</h3>
            <div className="notes-title_textarea">
              <textarea
                value={notes}
                onChange={handleNotesChange}
                onBlur={() => {
                  if (notes !== block.cooldown) {
                    debounceSave(exercises, blockDuration, timeSegments, notes);
                  }
                }}
                style={{
                  width: "100%",
                  background: "rgb(0 0 0 / 0%)",
                  border: "1px solid #444",
                  color: "#fff",
                  padding: 16,
                  borderRadius: 12,
                  minHeight: 100,
                  fontSize: 15,
                }}
                placeholder="Type coaching cues..."
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
  // Media Card
  // MediaCard component with delete functionality
  const MediaCard = ({ category, title, icon: Icon, accept }) => {
    const url = media[category];
    const isUploading = uploading[category];
    const [thumbnail, setThumbnail] = useState(null);
    const [isVideo, setIsVideo] = useState(false);
    const [isAudio, setIsAudio] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Generate thumbnail when URL changes
    useEffect(() => {
      if (url) {
        // Check if it's a video or audio
        const videoExtensions = [".mp4", ".webm", ".mov", ".avi", ".m3u8"];
        const audioExtensions = [".mp3", ".wav", ".ogg", ".m4a"];

        // Ensure url is a string before calling toLowerCase
        const urlString = typeof url === "string" ? url : "";

        const isVideoFile = videoExtensions.some((ext) =>
          urlString.toLowerCase().includes(ext),
        );
        const isAudioFile = audioExtensions.some((ext) =>
          urlString.toLowerCase().includes(ext),
        );

        setIsVideo(isVideoFile);
        setIsAudio(isAudioFile);

        if (isVideoFile) {
          // For videos, create a thumbnail using video element
          generateVideoThumbnail(urlString);
        } else if (isAudioFile) {
          // For audio, use a waveform placeholder or audio icon
          setThumbnail("audio");
        } else {
          // For images or unknown types
          setThumbnail("image");
        }
      } else {
        setThumbnail(null);
      }
    }, [url]);
    // Function to generate video thumbnail
    const generateVideoThumbnail = (videoUrl) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.src = videoUrl;
      video.currentTime = 1; // Capture at 1 second

      video.addEventListener("loadeddata", () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        try {
          const dataUrl = canvas.toDataURL();
          setThumbnail(dataUrl);
        } catch (error) {
          setThumbnail("video"); // Fallback to video icon
        }
      });

      video.addEventListener("error", () => {
        setThumbnail("video"); // Fallback to video icon
      });
    };
    // Handle file upload with better user feedback
    const handleFileClick = () => {
      if (!isUploading && !isDeleting) {
        fileInputs[category].current?.click();
      }
    };
    // Handle delete media
    const handleDeleteMedia = async () => {
      if (!url) return;

      setIsDeleting(true);
      const token = localStorage.getItem("accessToken");

      try {
        const response = await fetch(
          `${apiPath.baseURL}${apiPath.removeMedia}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              media_url: url,
              category: category,
              day: activeDay,
            }),
          },
        );

        const data = await response.json();

        if (data.status === 200) {
          message.success(`${title} deleted successfully!`);

          // Update state to remove the media
          setFullWeekData((prev) => {
            const newData = JSON.parse(JSON.stringify(prev));
            if (newData.full_week[activeDay].media) {
              delete newData.full_week[activeDay].media[category];
            }
            return newData;
          });
        } else {
          message.error(data.message || "Failed to delete media");
        }
      } catch (error) {
        message.error("Error deleting media");
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
      }
    };
    return (
      <div className="media-card">
        <input
          ref={fileInputs[category]}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => handleFileSelect(e, category)}
        />
        <div
          className="upload-content"
          style={{
            padding: 16,
            textAlign: "center",
            background: isUploading || isDeleting ? "#16213e" : "#1a1a2e",
            border: url ? "2px solid #00B5AD" : "2px dashed #444",
            borderRadius: 7,
            height: 200,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            cursor: isUploading || isDeleting ? "not-allowed" : "pointer",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
          }}
        >
          {/* Delete button - only shown when media exists */}
          {url && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "rgba(231, 76, 60, 0.9)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 10,
                transition: "all 0.3s ease",
              }}
              title={`Delete ${title}`}
            >
              <Trash2 size={14} />
            </button>
          )}
          {isUploading ? (
            <>
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: "3px solid #333",
                  borderTop: "3px solid #00B5AD",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              <div
                style={{ color: "#00B5AD", fontWeight: "bold", fontSize: 14 }}
              >
                Uploading...
              </div>
            </>
          ) : isDeleting ? (
            <>
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: "3px solid #333",
                  borderTop: "3px solid #e74c3c",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              <div
                style={{ color: "#e74c3c", fontWeight: "bold", fontSize: 14 }}
              >
                Deleting...
              </div>
            </>
          ) : url ? (
            <>
              {/* Image Preview */}
              {category === "background_image" ? (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 8,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <img
                    src={url}
                    alt={`${title} preview`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onClick={handleFileClick}
                  />
                </div>
              ) : thumbnail &&
                typeof thumbnail === "string" &&
                thumbnail.startsWith("data:image") ? (
                // Video thumbnail
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 8,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <img
                    src={thumbnail}
                    alt={`${title} thumbnail`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onClick={handleFileClick}
                  />
                  {isVideo && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(0,0,0,0.7)",
                        borderRadius: "50%",
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <PlayCircle size={24} color="#fff" />
                    </div>
                  )}
                </div>
              ) : thumbnail === "audio" ? (
                // Audio thumbnail with waveform visualization
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: 8,
                    position: "relative",
                  }}
                  onClick={handleFileClick}
                >
                  <Music size={40} color="#fff" />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 20,
                      width: "80%",
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                    }}
                  >
                    {[1, 2, 3, 4, 5, 4, 3, 2, 1].map((height, index) => (
                      <div
                        key={index}
                        style={{
                          width: 3,
                          height: height * 3,
                          background: "#fff",
                          borderRadius: 2,
                          animation: `wave 1.2s infinite ${index * 0.1}s`,
                        }}
                      />
                    ))}
                  </div>
                  <div
                    style={{
                      color: "#fff",
                      fontSize: 12,
                      marginTop: 10,
                      fontWeight: "bold",
                    }}
                  >
                    Audio File
                  </div>
                </div>
              ) : (
                // Generic uploaded file preview
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, #00B5AD 0%, #00a5a0 100%)",
                    borderRadius: 8,
                  }}
                  onClick={handleFileClick}
                >
                  <CheckCircle size={48} color="#fff" />
                  <div
                    style={{
                      color: "#fff",
                      fontSize: 14,
                      marginTop: 10,
                      fontWeight: "bold",
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      color: "rgba(255,255,255,0.8)",
                      fontSize: 11,
                      marginTop: 4,
                    }}
                  >
                    Click to change
                  </div>
                </div>
              )}
            </>
          ) : (
            // Empty state
            <>
              <Icon size={48} color="#666" onClick={handleFileClick} />
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                  color: "#ccc",
                }}
                onClick={handleFileClick}
              >
                {title}
              </div>
              <div
                style={{
                  color: "#666",
                  fontSize: 11,
                  marginTop: 4,
                }}
                onClick={handleFileClick}
              >
                Click to upload
              </div>
            </>
          )}
        </div>
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10002,
            }}
          >
            <div
              style={{
                background: "#1a1a2e",
                padding: "30px",
                borderRadius: "12px",
                border: "2px solid #e74c3c",
                minWidth: "300px",
                textAlign: "center",
                boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
              }}
            >
              <h3 style={{ color: "#e74c3c", marginBottom: "15px" }}>
                Delete {title}
              </h3>
              <p style={{ color: "#ccc", marginBottom: "20px" }}>
                Are you sure you want to delete this{" "}
                {category.includes("video")
                  ? "video"
                  : category.includes("audio")
                    ? "audio"
                    : "image"}
                ?
              </p>
              <p
                style={{
                  color: "#999",
                  fontSize: "12px",
                  marginBottom: "25px",
                }}
              >
                This action cannot be undone.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(false);
                  }}
                  style={{
                    padding: "8px 16px",
                    background: "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMedia();
                  }}
                  style={{
                    padding: "8px 16px",
                    background: "#e74c3c",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  if (loading)
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  if (error)
    return (
      <div className="error-screen">
        <p>{error}</p>
        <button onClick={fetchWeeklyPlan}>Retry</button>
      </div>
    );

  const ColorPicker = ({
    label,
    value,
    onApply,
    buttonColor,
    buttonTextColor = "#FCDC08",
  }) => {
    const [color, setColor] = useState(value);
    const colorRef = useRef(value);

    // Update when parent value changes
    useEffect(() => {
      setColor(value);
      colorRef.current = value;
    }, [value]);

    const handleChange = (e) => {
      const newColor = e.target.value;
      setColor(newColor);
      colorRef.current = newColor;
    };

    const handleApply = () => {
      console.log(`Applying ${label}:`, colorRef.current);
      onApply(colorRef.current);
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
          padding: "15px",
          background: "#2a2a3e",
          borderRadius: "10px",
          width: "200px",
          minHeight: "200px",
        }}
      >
        <div style={{ color: "#ccc", fontWeight: "600", fontSize: "14px" }}>
          {label}
        </div>

        <div
          style={{
            width: "70px",
            height: "70px",
            borderRadius: "10px",
            border: "3px solid #444",
            overflow: "hidden",
            cursor: "pointer",
            boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
          }}
        >
          <input
            type="color"
            value={color}
            onChange={handleChange}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              cursor: "pointer",
              transform: "scale(1.5)", // Makes color selection easier
            }}
            title={`Select ${label.toLowerCase()}`}
          />
        </div>

        <div
          style={{
            fontSize: "12px",
            color: "#888",
            fontFamily: "monospace",
            background: "#1a1a2e",
            padding: "4px 8px",
            borderRadius: "4px",
            minWidth: "100px",
            textAlign: "center",
          }}
        >
          {color}
        </div>

        <button
          onClick={handleApply}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            padding: "8px 16px",
            background: buttonColor,
            border: "none",
            borderRadius: "6px",
            color: buttonTextColor,
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "12px",
            width: "100%",
            transition: "all 0.2s",
          }}
          onMouseOver={(e) => (e.target.style.opacity = "0.9")}
          onMouseOut={(e) => (e.target.style.opacity = "1")}
        >
          {label.toUpperCase()}
        </button>
      </div>
    );
  };

  if (!currentDay) return <div>No workout for {activeDay}</div>;

  const blocks = [];
  // Get all blocks dynamically (A_BLOCK, B_BLOCK, C_BLOCK, D_BLOCK, etc.)
  Object.keys(currentDay.data || {}).forEach((key) => {
    if (key.endsWith("_BLOCK")) {
      blocks.push({
        ...currentDay.data[key],
        title: key.replace("_", " "),
        blockKey: key,
      });
    }
  });
  // Sort blocks alphabetically (A_BLOCK, B_BLOCK, C_BLOCK, D_BLOCK...)
  blocks.sort((a, b) => a.blockKey.localeCompare(b.blockKey));
  return (
    <div className="workout-container" style={{ position: "relative" }}>
      {media.pre_hype_audio && (
        <audio
          key={`pre_hype_${activeDay}`}
          ref={audioRefs.pre_hype}
          src={media.pre_hype_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, pre_hype: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, pre_hype: false }))
          }
          onPlay={() =>
            setAudioPlaying((prev) => ({ ...prev, pre_hype: true }))
          }
        />
      )}
      {media.warmup_audio && (
        <audio
          key={`warmup_${activeDay}`}
          ref={audioRefs.warmup}
          src={media.warmup_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, warmup: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, warmup: false }))
          }
          onPlay={() => setAudioPlaying((prev) => ({ ...prev, warmup: true }))}
        />
      )}
      {media.workout_audio && (
        <audio
          key={`workout_${activeDay}`}
          ref={audioRefs.workout}
          src={media.workout_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, workout: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, workout: false }))
          }
          onPlay={() => setAudioPlaying((prev) => ({ ...prev, workout: true }))}
        />
      )}
      {media.preview_audio && (
        <audio
          key={`preview_${activeDay}`}
          ref={audioRefs.preview}
          src={media.preview_audio}
          loop
          preload="auto"
          onEnded={() =>
            setAudioPlaying((prev) => ({ ...prev, preview: false }))
          }
          onPause={() =>
            setAudioPlaying((prev) => ({ ...prev, preview: false }))
          }
          onPlay={() => setAudioPlaying((prev) => ({ ...prev, preview: true }))}
        />
      )}
      <Container>
        <div className="workout-inner">
          <SessionHeader />

          {blocks.map((block, i) => (
            <WorkoutBlock key={i} block={block} blockKey={block.blockKey} />
          ))}

          {/* Add New Block Button */}
          <div
            style={{
              textAlign: "center",
              margin: "30px 0",
              padding: "20px",
              border: "2px dashed #004aad",
              borderRadius: "12px",
            }}
          >
            <button
              onClick={openAddBlockModal}
              style={{
                background: "#004aad",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "0 auto",
                color: "#fcdc08",
              }}
            >
              <Plus size={20} />
              Add New Block
            </button>
          </div>
          <div
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: "20px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              className="action-button"
              style={{
                background: "#004aad",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#fcdc08",
              }}
              disabled={loading || !fullWeekData}
            >
              <Download size={16} />
              Download Excel
            </button>

            <button
              className="action-button"
              onClick={() =>
                document.getElementById("excel-upload-input").click()
              }
              style={{
                background: "#004aad",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#fcdc08",
              }}
              disabled={loading}
            >
              <Upload size={16} />
              Upload Excel
            </button>

            <input
              id="excel-upload-input"
              type="file"
              accept=".xlsx,.xls,.xlsm"
              style={{ display: "none" }}
            />
          </div>
          <div style={{ margin: "40px 0" }}>
            <h2
              style={{
                color: "rgb(168, 168, 168)",
                marginBottom: 24,
                fontSize: 28,
                textAlign: "center",
              }}
            >
              Media for {activeDay}
            </h2>
            <Row gutter={[16, 22]}>
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Pre-Hype Video</h4>
                <MediaCard
                  category="pre_hype_video"
                  title="Pre-Hype Video"
                  icon={PlayCircle}
                  accept="video/*"
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Warm-Up Video</h4>
                <MediaCard
                  category="warmup_video"
                  title="Warm-Up Video"
                  icon={PlayCircle}
                  accept="video/*"
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Preview Audio</h4>
                <MediaCard
                  category="preview_audio"
                  title="Preview Audio"
                  icon={Music}
                  accept="audio/*"
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Pre-Hype Audio</h4>
                <MediaCard
                  category="pre_hype_audio"
                  title="Pre-Hype Audio"
                  icon={Music}
                  accept="audio/*"
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Warm-Up Audio</h4>
                <MediaCard
                  category="warmup_audio"
                  title="Warm-Up Audio"
                  icon={Music}
                  accept="audio/*"
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Workout Audio</h4>
                <MediaCard
                  category="workout_audio"
                  title="Workout Audio"
                  icon={Music}
                  accept="audio/*"
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <h4 className="for-video-title">Background Image</h4>
                <MediaCard
                  category="background_image"
                  title="Background Image"
                  icon={Image}
                  accept="image/*"
                />
              </Col>
            </Row>
          </div>
          <div
            style={{
              margin: "40px 0",
              padding: "20px",
              background: "#1a1a2e",
              borderRadius: "12px",
            }}
          >
            <h3 style={{ color: "#00B5AD", marginBottom: "20px" }}>
              Workout Completion Settings
            </h3>
            <div
              style={{
                display: "flex",
                gap: "40px",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  width: "45%",
                  maxWidth: "500px",
                }}
              >
                <div>
                  <label
                    style={{
                      color: "#fff",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Top Completion Message:
                  </label>
                  <Input
                    value={completionTopMessage}
                    onChange={(e) => setCompletionTopMessage(e.target.value)}
                    placeholder="Enter top completion message..."
                    style={{
                      background: "#2a2a3e",
                      border: "2px solid #00B5AD",
                      color: "#fff",
                      padding: "12px",
                      borderRadius: "8px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      color: "#fff",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Completion Message:
                  </label>
                  <Input
                    value={completionMessage}
                    onChange={(e) => setCompletionMessage(e.target.value)}
                    placeholder="Enter completion message..."
                    style={{
                      background: "#2a2a3e",
                      border: "2px solid #00B5AD",
                      color: "#fff",
                      padding: "12px",
                      borderRadius: "8px",
                      width: "100%",
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    autoSave({
                      new_completion_message: completionMessage,
                      new_completion_top_message: completionTopMessage,
                      new_workout_transition_message: workoutTransitionMessage,
                    });
                    message.success("Completion message saved!");
                  }}
                  style={{
                    padding: "12px 24px",
                    background: "#00B5AD",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    alignSelf: "flex-start",
                  }}
                >
                  Save Messages
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  width: "45%",
                  maxWidth: "500px",
                }}
              >
                <div>
                  <label
                    style={{
                      color: "#fff",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Workout Transition Message:
                  </label>
                  <Input
                    value={workoutTransitionMessage}
                    onChange={(e) =>
                      setWorkoutTransitionMessage(e.target.value)
                    }
                    placeholder="Enter workout transition message..."
                    style={{
                      background: "#2a2a3e",
                      border: "2px solid #00B5AD",
                      color: "#fff",
                      padding: "12px",
                      borderRadius: "8px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* WORKOUT SETTINGS SECTION (Color Pickers) */}
          <div
            style={{
              margin: "40px 0",
              padding: "30px",
              background: "#1a1a2e",
              borderRadius: "12px",
              border: "2px solid #00B5AD",
            }}
          >
            <h3
              style={{
                color: "#00B5AD",
                marginBottom: "30px",
                fontSize: "24px",
                textAlign: "center",
              }}
            >
              WORKOUT SETTINGS
            </h3>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexGrow: 1,
                gap: "40px",
                flexWrap: "wrap",
              }}
            >
              <ColorPicker
                label="Background Color"
                value={backgroundColor}
                onApply={handleBackgroundColorChange}
                buttonColor="#000000"
                buttonTextColor="#fff"
              />

              <ColorPicker
                label="Apply Text Color"
                value={tempTextColor}
                onApply={handleTextColorChange}
                buttonColor="#00B5AD"
                buttonTextColor="#fff"
              />

              <ColorPicker
                label="Apply Border Color"
                value={tempBorderColor}
                onApply={handleBorderColorChange}
                buttonColor="#FCDC08"
                buttonTextColor="#000"
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexGrow: 1,
                gap: "40px",
                flexWrap: "wrap",
                marginTop: "30px",
              }}
            >
              {/* Font 1 selector */}
              <FontSelector
                title="Font A"
                selectedFont={font1}
                tempFontColor={tempFont1Color}
                onApply={handleFont1Apply}
                label="Font A Color"
                buttonColor="#ffffff"
                buttonTextColor="#000"
              />

              {/* Font 2 selector */}
              <FontSelector
                title="Font B"
                selectedFont={font2}
                tempFontColor={tempFont2Color}
                onApply={handleFont2Apply}
                label="Font B Color"
                buttonColor="#ffffff"
                buttonTextColor="#000"
              />

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                  padding: "15px",
                  background: "#2a2a3e",
                  borderRadius: "10px",
                  width: "200px",
                  minHeight: "200px",
                }}
              >
                <div
                  style={{ color: "#ccc", fontWeight: "600", fontSize: "14px" }}
                >
                  Theme Color
                </div>

                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "10px",
                    border: "3px solid #444",
                    overflow: "hidden",
                    cursor: "pointer",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  }}
                >
                  <input
                    type="color"
                    value={selectedColor}
                    onChange={(e) => setTempColor(e.target.value)}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      cursor: "pointer",
                      transform: "scale(1.5)",
                    }}
                    title="Select theme color for this day"
                  />
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    color: "#888",
                    fontFamily: "monospace",
                    background: "#1a1a2e",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    minWidth: "100px",
                    textAlign: "center",
                  }}
                >
                  {selectedColor}
                </div>

                <button
                  onClick={() => handleColorChange(tempColor)}
                  style={{
                    padding: "8px 16px",
                    background: "#4CAF50",
                    border: "none",
                    borderRadius: "6px",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "12px",
                    width: "100%",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => (e.target.style.opacity = "0.9")}
                  onMouseOut={(e) => (e.target.style.opacity = "1")}
                >
                  APPLY THEME COLOR
                </button>
              </div>
            </div>

            {/* <div style={{
                marginTop: "30px",
                padding: "15px",
                background: "#2a2a3e",
                borderRadius: "8px",
                textAlign: "center"
              }}>
                <p style={{ color: "#ccc", fontSize: "14px", marginBottom: "10px" }}>
                  These colors will be applied to the workout display for {activeDay}
                </p>
                <p style={{ color: "#888", fontSize: "12px" }}>
                  Text Color: <span style={{ color: textColor, fontWeight: "bold" }}>{textColor}</span> | 
                  Border Color: <span style={{ color: borderColor, fontWeight: "bold" }}>{borderColor}</span>
                </p>
              </div> */}
          </div>
        </div>
      </Container>
      {/* Saved Indicator */}
      <div
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          width: 16,
          height: 16,
          background: "#00B5AD",
          borderRadius: "50%",
          opacity: justSaved ? 1 : 0,
          transition: "opacity 0.4s",
          boxShadow: "0 0 20px #00B5AD",
          zIndex: 9999,
        }}
      />

      {/* Add Block Modal */}
      {showAddBlockModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "#1a1a2e",
              padding: "40px",
              borderRadius: "16px",
              border: "3px solid #00B5AD",
              minWidth: "400px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <h2
              style={{
                color: "#00B5AD",
                marginBottom: "30px",
                textAlign: "center",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              Add New Block
            </h2>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  color: "#fff",
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Block Name:
              </label>
              <Input
                value={newBlockName}
                onChange={(e) => setNewBlockName(e.target.value.toUpperCase())}
                placeholder="D_BLOCK"
                style={{
                  background: "#2a2a3e",
                  border: "2px solid #00B5AD",
                  color: "#fff",
                  fontSize: "16px",
                  padding: "12px",
                  borderRadius: "8px",
                }}
              />
            </div>

            <div style={{ marginBottom: "30px" }}>
              <label
                style={{
                  color: "#fff",
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                }}
              >
                Block Title:
              </label>
              <Input
                value={newBlockTitle}
                onChange={(e) => setNewBlockTitle(e.target.value)}
                placeholder="Cardio Block"
                style={{
                  background: "#2a2a3e",
                  border: "2px solid #00B5AD",
                  color: "#fff",
                  fontSize: "16px",
                  padding: "12px",
                  borderRadius: "8px",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => {
                  setShowAddBlockModal(false);
                  setNewBlockName("");
                  setNewBlockTitle("");
                }}
                style={{
                  padding: "12px 24px",
                  background: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
              <button
                onClick={addNewBlock}
                style={{
                  padding: "12px 24px",
                  background: "#00B5AD",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
                }}
              >
                Add Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Block Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "#1a1a2e",
              padding: "40px",
              borderRadius: "16px",
              border: "3px solid #e74c3c",
              minWidth: "400px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              textAlign: "center",
            }}
          >
            <h2
              style={{
                color: "#e74c3c",
                marginBottom: "20px",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              Delete Block
            </h2>

            <p
              style={{
                color: "#fff",
                fontSize: "16px",
                marginBottom: "30px",
              }}
            >
              Are you sure you want to delete <strong>{blockToDelete}</strong>?
            </p>

            <p
              style={{
                color: "#ccc",
                fontSize: "14px",
                marginBottom: "30px",
              }}
            >
              This action cannot be undone.
            </p>

            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setBlockToDelete("");
                }}
                style={{
                  padding: "12px 24px",
                  background: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteBlock}
                style={{
                  padding: "12px 24px",
                  background: "#e74c3c",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 15px rgba(231, 76, 60, 0.4)",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShadowView;
